/**
 * A "corrida dos carrinhos" que a gerente criou: cada representante é um carro
 * numa faixa da pista; a frente do carro marca o valor. Os 3 primeiros são
 * ouro, prata e bronze; quem ainda não vendeu fica "na largada", em cinza.
 * A linha tracejada amarela é o ritmo esperado para hoje. Em cima de cada carro,
 * a foto do representante (Cadastros → Representantes) ou, sem foto, as iniciais.
 *
 * Lê: só as props.
 * Usado por: telas/Painel.tsx (e pela exportação de imagem).
 */
import { useId, useState } from 'react';
import { Carrinho, ALTURA_CARRO, LARGURA_CARRO } from './Carrinho.tsx';
import type { Cores } from './cores.ts';
import { Dica } from './Dica.tsx';
import { Moldura, type Cabecalho } from './Moldura.tsx';
import { abreviarNome, limitar } from './svg.ts';

export interface ItemCorrida {
  chave: string;
  nome: string;
  valor: number;
  /** O que aparece escrito na frente do carro (ex.: "21,3%"). */
  texto: string;
  iniciais: string;
  /** Endereço da foto (null = só as iniciais). */
  foto?: string | null;
  /** 1, 2, 3... ou null para quem está na largada. */
  posicao: number | null;
  detalhes: string[];
  aoAbrir?: () => void;
}

interface Props {
  itens: ItemCorrida[];
  maximo: number;
  marcasEixo: number[];
  formatarMarca: (valor: number) => string;
  marcador?: { valor: number; rotulo: string };
  largura: number;
  cores: Cores;
  descricao: string;
  cabecalho?: Cabecalho;
}

function corDoCarro(posicao: number | null, cores: Cores): string {
  if (posicao === null) return cores.carroParado;
  return [cores.ouro, cores.prata, cores.bronze][posicao - 1] ?? cores.carro;
}

export function CorridaCarrinhos({ itens, maximo, marcasEixo, formatarMarca, marcador, largura, cores, descricao, cabecalho }: Props) {
  const [ativo, setAtivo] = useState<number | null>(null);
  // Um recorte redondo para todas as fotos (relativo ao tamanho de cada imagem).
  const recorte = `foto-redonda-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const margem = cabecalho ? 32 : 0;
  const escala = limitar(largura / 1060, 0.62, 1);
  const faixa = Math.round(84 * escala);
  const topo = 46;
  const larguraNome = Math.round(limitar(largura * 0.16, 78, 150));
  const pistaX0 = margem + larguraNome + 10;
  const pistaX1 = largura - margem - 4;
  const larguraCarro = LARGURA_CARRO * escala;
  const largadaX = pistaX0 + larguraCarro + 16 * escala;
  const chegadaX = pistaX1 - 86 * escala;
  const x = (valor: number) => largadaX + (limitar(valor, 0, maximo) / maximo) * (chegadaX - largadaX);
  const pistaY0 = topo - 6;
  const pistaY1 = topo + itens.length * faixa + 6;
  const altura = pistaY1 + 6;
  const fonteNome = Math.max(12, Math.round(14 * escala));

  const ativoItem = ativo === null ? null : itens[ativo];

  return (
    <div className="grafico">
      <Moldura largura={largura} altura={altura} cores={cores} descricao={descricao} cabecalho={cabecalho}>
        <defs>
          <clipPath id={recorte} clipPathUnits="objectBoundingBox">
            <circle cx={0.5} cy={0.5} r={0.5} />
          </clipPath>
        </defs>
        {/* pista */}
        <rect x={pistaX0} y={pistaY0} width={pistaX1 - pistaX0} height={pistaY1 - pistaY0} rx={14} fill={cores.pista} />

        {/* marcas do eixo (em cima da pista) e linhas da grade */}
        {marcasEixo.map((marca, i) => {
          // Com pouco espaço entre as marcas, escreve só uma sim, outra não.
          const espaco = marcasEixo.length > 1 ? x(marcasEixo[1] ?? 0) - x(marcasEixo[0] ?? 0) : 999;
          const escrever = espaco >= 38 || i % 2 === 0;
          return (
            <g key={`eixo-${marca}`}>
              {marca > 0 ? <line x1={x(marca)} x2={x(marca)} y1={pistaY0} y2={pistaY1} stroke={cores.pistaGrade} strokeWidth={1} /> : null}
              {escrever ? (
                <text x={x(marca)} y={topo - 14} fontSize={11} textAnchor="middle" fill={cores.tinta3}>
                  {formatarMarca(marca)}
                </text>
              ) : null}
            </g>
          );
        })}

        {/* separação das faixas */}
        {itens.slice(1).map((item, i) => (
          <line key={`faixa-${item.chave}`} x1={pistaX0 + 10} x2={pistaX1 - 10} y1={topo + (i + 1) * faixa} y2={topo + (i + 1) * faixa} stroke={cores.pistaFaixa} strokeWidth={1.4} strokeDasharray="10 8" />
        ))}

        {/* linha de largada (0%) */}
        <line x1={largadaX} x2={largadaX} y1={pistaY0} y2={pistaY1} stroke={cores.pistaTexto} strokeWidth={2.5} />

        {itens.map((item, indice) => {
          const yFaixa = topo + indice * faixa;
          const centro = yFaixa + faixa / 2;
          const frente = x(item.valor);
          const yCarro = yFaixa + faixa - ALTURA_CARRO * escala - 12 * escala;
          const xCarro = frente - larguraCarro;
          const raio = 18 * escala;
          const avatarX = xCarro + 52 * escala;
          const avatarY = yCarro - 2 * escala;
          const andando = item.valor > 0;
          return (
            <g
              key={item.chave}
              className="grafico-linha"
              tabIndex={item.aoAbrir ? 0 : undefined}
              role={item.aoAbrir ? 'link' : undefined}
              aria-label={item.detalhes.join('. ')}
              onPointerEnter={() => setAtivo(indice)}
              onPointerLeave={() => setAtivo(null)}
              onFocus={() => setAtivo(indice)}
              onBlur={() => setAtivo(null)}
              onClick={item.aoAbrir}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter') item.aoAbrir?.();
              }}
            >
              <rect x={margem} y={yFaixa} width={largura - margem * 2} height={faixa} fill="transparent" />
              {ativo === indice ? <rect x={pistaX0 + 4} y={yFaixa + 3} width={pistaX1 - pistaX0 - 8} height={faixa - 6} rx={10} fill="rgba(255,255,255,0.06)" /> : null}
              <text x={margem + larguraNome} y={centro} dy="0.35em" textAnchor="end" fontSize={fonteNome} fontWeight={700} fill={cores.tinta}>
                {abreviarNome(item.nome, larguraNome, fonteNome)}
              </text>
              {andando && xCarro - 30 * escala > pistaX0
                ? [0, 1, 2].map((traco) => (
                    <line
                      key={traco}
                      x1={xCarro - (6 + traco * 3) * escala}
                      x2={xCarro - (22 + traco * 5) * escala}
                      y1={yCarro + (12 + traco * 5) * escala}
                      y2={yCarro + (12 + traco * 5) * escala}
                      stroke="rgba(255,255,255,0.45)"
                      strokeWidth={2 * escala}
                      strokeLinecap="round"
                    />
                  ))
                : null}
              <Carrinho x={xCarro} y={yCarro} escala={escala} cor={corDoCarro(item.posicao, cores)} posicao={item.posicao} cores={cores} />
              <circle cx={avatarX} cy={avatarY} r={raio} fill={cores.marcaSuave} stroke="#ffffff" strokeWidth={2.5 * escala} />
              <text x={avatarX} y={avatarY} dy="0.36em" textAnchor="middle" fontSize={Math.round(13 * escala)} fontWeight={800} fill={cores.marcaForte}>
                {item.iniciais}
              </text>
              {/* A foto cobre as iniciais; se não carregar, as iniciais continuam lá embaixo. */}
              {item.foto ? (
                <image href={item.foto} x={avatarX - raio} y={avatarY - raio} width={raio * 2} height={raio * 2} clipPath={`url(#${recorte})`} preserveAspectRatio="xMidYMid slice" />
              ) : null}
              {item.foto ? <circle cx={avatarX} cy={avatarY} r={raio} fill="none" stroke="#ffffff" strokeWidth={2.5 * escala} /> : null}
              <text x={frente + 10 * escala} y={yCarro + 22 * escala} fontSize={Math.max(12, Math.round(15 * escala))} fontWeight={700} fill={andando ? cores.pistaTexto : cores.pistaTextoApagado}>
                {item.texto}
              </text>
            </g>
          );
        })}

        {/* ritmo esperado para hoje */}
        {marcador && marcador.valor <= maximo ? (
          <g pointerEvents="none">
            <line x1={x(marcador.valor)} x2={x(marcador.valor)} y1={pistaY0} y2={pistaY1} stroke={cores.ritmoPista} strokeWidth={2.5} strokeDasharray="7 6" />
            <text x={x(marcador.valor)} y={14} fontSize={12} fontWeight={800} textAnchor={x(marcador.valor) > largura - 140 ? 'end' : 'middle'} fill={cores.tinta2}>
              {marcador.rotulo}
            </text>
          </g>
        ) : null}
      </Moldura>
      {ativoItem && !cabecalho ? (
        <Dica x={x(ativoItem.valor)} y={topo + (ativo ?? 0) * faixa + faixa - 6} larguraDisponivel={largura} linhas={ativoItem.detalhes} />
      ) : null}
    </div>
  );
}
